import { MoreHorizontal } from 'lucide-react';
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface DropdownMenuContextValue {
  close: () => void;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue>({ close: () => {} });

interface DropdownMenuProps {
  /** Content rendered inside the trigger button. Defaults to a "..." icon. */
  trigger?: ReactNode;
  /** Horizontal alignment of the menu relative to the trigger. */
  align?: 'left' | 'right';
  /** Extra classes for the trigger button. */
  buttonClassName?: string;
  /** Accessible label for the trigger button. */
  label?: string;
  children: ReactNode;
}

const MENU_WIDTH = 176; // matches min-w-44 (11rem)
const GAP = 4;
const MARGIN = 8;

interface MenuPosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

export function DropdownMenu({ trigger, align = 'right', buttonClassName, label = 'Más opciones', children }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPosition>({ top: 0, left: 0, placement: 'bottom' });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Measure the trigger and place the menu in viewport coordinates (fixed),
  // so it escapes any ancestor `overflow-hidden` / stacking context.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    let left = align === 'right' ? rect.right - MENU_WIDTH : rect.left;
    left = Math.max(MARGIN, Math.min(left, innerWidth - MENU_WIDTH - MARGIN));

    const spaceBelow = innerHeight - rect.bottom - GAP - MARGIN;
    const spaceAbove = rect.top - GAP - MARGIN;
    const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;

    setPos({
      top: openAbove ? rect.top - GAP : rect.bottom + GAP,
      left,
      placement: openAbove ? 'top' : 'bottom',
    });
  }, [open, align]);

  // Close on outside click and Escape. The menu lives in a portal, so check both refs.
  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // A fixed-positioned menu must follow or dismiss on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={buttonClassName ?? 'admin-btn text-[11px] py-1 px-2 flex items-center justify-center'}
      >
        {trigger ?? <MoreHorizontal className="w-4 h-4" />}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: MENU_WIDTH,
              transform: pos.placement === 'top' ? 'translateY(-100%)' : 'none',
              zIndex: 999999,
            }}
            className="rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            <DropdownMenuContext.Provider value={{ close: () => setOpen(false) }}>{children}</DropdownMenuContext.Provider>
          </div>,
          document.body
        )}
    </div>
  );
}

interface DropdownMenuItemProps {
  onClick?: () => void;
  icon?: ReactNode;
  variant?: 'default' | 'success' | 'danger';
  active?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export function DropdownMenuItem({
  onClick,
  icon,
  variant = 'default',
  active = false,
  disabled = false,
  children,
}: DropdownMenuItemProps) {
  const { close } = useContext(DropdownMenuContext);

  const variantClasses =
    variant === 'success'
      ? 'text-[#2d7a4f] hover:bg-[#eaf5ee]'
      : variant === 'danger'
        ? 'text-[#c0392b] hover:bg-[#fdf0ee]'
        : 'text-gray-700 hover:bg-gray-50';

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        onClick?.();
        close();
      }}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${variantClasses} ${
        active ? 'bg-gray-50' : ''
      }`}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
    </button>
  );
}

export function DropdownMenuSeparator() {
  return <div className="my-1 h-px bg-gray-100" />;
}
